import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Typography,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  App,
} from "antd";
import dayjs from "dayjs";
import { fetchAllEmployees } from "../../attendanceOverviewThunk";
import { useDispatch, useSelector } from "react-redux";
import { selectEmployees } from "../../attendanceOverviewSelectors";

const { Text } = Typography;
const { Option } = Select;

const BulkMarkPresentModal = ({
  visible,
  onClose,
  onSubmit,  
  restrictedHolidays = [],
  weekEndDays = [],
  isDisabled,
}) => {
  const { modal } = App.useApp();

  const [dateTime, setDateTime] = useState(dayjs());
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [notes, setNotes] = useState("");

  const dispatch = useDispatch();

  const employees = useSelector(selectEmployees);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setDateTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

   useEffect(() => {      
      dispatch(fetchAllEmployees());
    }, [dispatch]);

  // Reset when closed
  useEffect(() => {
    if (!visible) {
      setSelectedEmployees([]);
      setFromDate(null);
      setToDate(null);
      setNotes("");
    }
  }, [visible]);


  const handleSave = () => {
    if (!selectedEmployees.length)
      return modal.warning({ title: "Select Employees", centered: true });

    if (!fromDate)
      return modal.warning({ title: "Select From Date", centered: true });

    if (!toDate)
      return modal.warning({ title: "Select To Date", centered: true });

    if (!notes?.trim())
      return modal.warning({ title: "Notes is required", centered: true });

    const formattedFrom = dayjs(fromDate).format("YYYY-MM-DD");
    const formattedTo = dayjs(toDate).format("YYYY-MM-DD");

    if (formattedFrom > formattedTo)
      return modal.warning({
        title: "The 'From' date cannot be later than the 'To' date.",
        centered: true,
      });

    onSubmit({
      emp_ids: selectedEmployees,
      from_date: formattedFrom,
      to_date: formattedTo,
      notes,
    });
  };

  const disableDate = (current) => {
  const restrictedDates = restrictedHolidays.map(
    (holiday) => holiday.restricted_holiday_date
  );

  return (
    restrictedDates.includes(current.format("YYYY-MM-DD")) ||
    weekEndDays.includes(current.day())
  );
};


  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="leave-modal"
    >
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginTop: "1rem" }}>
        <Col>
          <Text strong className="attendance-modal-title">
            Bulk Mark Present
          </Text>
        </Col>
        <Col>
          <Text className="attendance-modal-time">
            {dateTime.format("DD-MMM-YYYY hh:mm:ss A")}
          </Text>
        </Col>
      </Row>

      {/* Employee Selection */}
      <Row gutter={16} style={{ marginTop: "2rem" }}>
        <Col span={24}>
          <label className="form-label">
            Select Employees <span className="form-span">*</span>
          </label>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Select Employees"
            maxTagCount="responsive"
            maxTagPlaceholder={(omittedValues) => (
                <span title={omittedValues.map((v) => v.label).join(", ")}>
                    +{omittedValues.length}
                </span>
            )}
            value={selectedEmployees}
            onChange={setSelectedEmployees}
            optionFilterProp="children"
          >
            {employees.map((emp) => (
              <Option key={emp.id} value={emp.id}>
                {emp.user_name}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* Date Pickers (Same as LeaveModal) */}
      <Row gutter={16} style={{ marginTop: "1.5rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            From Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={fromDate}
            onChange={(val) => setFromDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            To Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={toDate}
            onChange={(val) => setToDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
          />
        </Col>
      </Row>

      {/* Notes */}
      <div style={{ marginTop: "1.5rem" }}>
        <label className="form-label">
          Notes <span className="form-span">*</span>
        </label>
        <Input
          placeholder="Reason for bulk marking"
          value={notes}
          onChange={(e) => {
            if (e.target.value.length <= 50) {
              setNotes(e.target.value);
            }
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {notes.length}/50 characters
        </Text>
      </div>

      {/* Buttons */}
      <div className="attendance-buttons">
        <Button onClick={onClose}>Discard</Button>
        <Button
          type="primary"
          disabled={
            isDisabled ||
            !selectedEmployees.length ||
            !fromDate ||
            !toDate ||
            !notes
          }
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default BulkMarkPresentModal;
